import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { CandidateClaimBadge, CandidateClaimInfo, candidateRowClass } from "../components/CandidateClaimBadge";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowClockwise } from "@phosphor-icons/react";

function formatIDR(num) {
  if (!num && num !== 0) return "-";
  return "Rp " + new Intl.NumberFormat("id-ID").format(num);
}

export default function RecordDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRecord(); }, [id]);
  const loadRecord = async () => {
    try { const { data } = await API.get(`/records/${id}`); setRecord(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleRetry = async () => {
    try { await API.post(`/records/${id}/retry`); await loadRecord(); }
    catch (e) { alert("Retry gagal"); }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-400 font-mono">Loading...</div>;
  if (!record) return <div className="p-8 text-sm text-red-500">Record tidak ditemukan</div>;

  return (
    <div data-testid="record-detail-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-zinc-900"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Detail Record</h1>
          <p className="text-xs font-mono text-zinc-400 mt-0.5">{record.username} | {record.transaction_id}</p>
        </div>
        {["panel_error", "sheet_error", "not_found"].includes(record.final_status) && (
          <Button data-testid="retry-record-btn" onClick={handleRetry} variant="outline" className="rounded-sm gap-2"><ArrowClockwise size={16} /> Retry</Button>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <IC label="Status" value={<StatusBadge status={record.final_status} />} />
        <IC label="Score" value={record.confidence_score} mono />
        <IC label="Tipe" value={record.transaction_type} />
        <IC label="Klasifikasi" value={record.classification === "payment_gateway" ? "Payment Gateway" : "Manual"} />
        <IC label="Nominal" value={formatIDR(record.amount)} mono />
        <IC label="Status Panel" value={record.status_panel} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <IC label="Username" value={record.username} mono />
        <IC label="Account Name" value={record.account_name} />
        <IC label="Fund Method" value={record.fund_method} />
        <IC label="Bank Details" value={record.bank_details} />
      </div>

      {record.classification === "payment_gateway" && (
        <div className="border border-violet-200 bg-violet-50 rounded-sm p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-600 mb-1">Payment Gateway</p>
          <p className="text-sm text-violet-800">Transaksi diproses via <strong>{record.pg_panel_name || "PG"}</strong>. {record.pg_verified ? "Terverifikasi di panel PG." : "Menunggu verifikasi."}</p>
        </div>
      )}

      {record.tolerance_applied && (
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-xs text-amber-700">
          Toleransi nominal diterapkan. Nominal di Sheet mungkin berbeda karena biaya admin.
        </div>
      )}

      {record.split_detected && (
        <div className="border border-cyan-200 bg-cyan-50 rounded-sm p-3 text-xs text-cyan-700">
          Split transaksi terdeteksi. Transaksi ini dipecah menjadi beberapa entry di Google Sheet.
        </div>
      )}

      {record.error_text && (
        <div className="border border-red-200 bg-red-50 rounded-sm p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-red-600 mb-1">Error</p>
          <p className="text-sm text-red-700 font-mono">{record.error_text}</p>
        </div>
      )}

      {/* C-lite Demotion Banner */}
      {record.demoted_by_clite && (
        <div
          data-testid="clite-demoted-banner"
          className="border-l-4 border-amber-500 bg-amber-50 p-4 rounded-sm"
        >
          <div className="flex items-start gap-3">
            <span className="text-amber-700 text-lg leading-none mt-0.5">⚠</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900 mb-1">
                Demoted oleh C-lite Resolution (Strict 1-to-1 Enforcement)
              </p>
              <p className="text-xs text-amber-800 leading-relaxed mb-2">
                Status final record ini di-set <span className="font-mono font-semibold">not_found</span> walau pipeline
                matcher sempat menemukan kandidat. Sebabnya: setiap baris sheet hanya boleh
                di-claim oleh <span className="font-semibold">SATU</span> panel record. Semua kandidat di bawah
                sudah di-claim panel record lain dengan match score lebih tinggi atau time-gap lebih dekat.
              </p>
              {record.demoted_reason && (
                <p className="text-[11px] text-amber-700 font-mono bg-amber-100/60 px-2 py-1.5 rounded-sm">
                  {record.demoted_reason}
                </p>
              )}
              {record.demoted_winner_id && (
                <div className="relative inline-block group mt-2" data-testid="clite-winner-link-wrapper">
                  <a
                    href={`/records/${record.demoted_winner_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="clite-winner-link"
                    className="inline-flex items-center gap-1 text-[11px] text-amber-800 hover:text-amber-900 font-mono border border-amber-300 hover:bg-amber-100 px-2 py-1 rounded-sm transition-colors"
                  >
                    <span>Lihat panel winner</span>
                    {record.demoted_winner_username && (
                      <span className="font-bold">({record.demoted_winner_username})</span>
                    )}
                    <span aria-hidden>↗</span>
                  </a>
                  {record.demoted_winner_info && (
                    <div
                      data-testid="clite-winner-tooltip"
                      className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 w-[320px] bg-white border border-zinc-300 rounded-sm shadow-lg p-3 text-[11px] font-mono"
                    >
                      <div className="text-zinc-500 uppercase tracking-widest text-[9px] mb-2 font-semibold">
                        Panel Winner Preview
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between gap-2">
                          <span className="text-zinc-500">Username:</span>
                          <span className="font-bold text-zinc-900">{record.demoted_winner_info.username || '-'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-zinc-500">Amount:</span>
                          <span className="text-zinc-900">Rp {(record.demoted_winner_info.amount || 0).toLocaleString('id-ID')}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-zinc-500">Tanggal:</span>
                          <span className="text-zinc-900">{record.demoted_winner_info.transaction_date || '-'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-zinc-500">Sheet Row:</span>
                          <span className="text-zinc-900">
                            {record.demoted_winner_info.matched_sheet_tab || '-'} / {record.demoted_winner_info.matched_row_reference || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-zinc-500">Score:</span>
                          <span className="text-emerald-600 font-bold">{record.demoted_winner_info.confidence_score || 0}%</span>
                        </div>
                        {record.demoted_winner_info.time_gap_seconds !== undefined && (
                          <div className="flex justify-between gap-2 border-t border-zinc-100 pt-1 mt-1">
                            <span className="text-zinc-500">Gap dari record ini:</span>
                            <span className="text-amber-700 font-bold">
                              {record.demoted_winner_info.time_gap_seconds < 60
                                ? `${record.demoted_winner_info.time_gap_seconds}s`
                                : record.demoted_winner_info.time_gap_seconds < 3600
                                ? `${Math.floor(record.demoted_winner_info.time_gap_seconds / 60)}m ${record.demoted_winner_info.time_gap_seconds % 60}s`
                                : `${Math.floor(record.demoted_winner_info.time_gap_seconds / 3600)}h ${Math.floor((record.demoted_winner_info.time_gap_seconds % 3600) / 60)}m`}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between gap-2">
                          <span className="text-zinc-500">Status:</span>
                          <span className="text-zinc-900">{record.demoted_winner_info.final_status || '-'}</span>
                        </div>
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-2 pt-2 border-t border-zinc-100">
                        Klik link untuk buka detail lengkap di tab baru
                      </div>
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-amber-700 mt-2">
                <span className="font-semibold">Saran:</span> Kemungkinan transaksi ini retry/duplikasi
                yang tidak ada baris pasangannya di sheet, atau perlu dicek di tab GANTUNG/PENDING (Phase 1.5
                fallback otomatis akan re-search jika status withdraw).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* GANTUNG Verification Evidence */}
      {(record.gantung_verification_status) && (
        <div className={`border rounded-sm ${
          record.gantung_verification_status === "verified"
            ? "border-emerald-300 bg-emerald-50/40"
            : "border-amber-300 bg-amber-50/40"
        }`} data-testid="gantung-verification-section">
          <div className={`px-4 py-3 border-b ${
            record.gantung_verification_status === "verified" ? "border-emerald-200" : "border-amber-200"
          }`}>
            <h2 className="text-sm font-semibold text-zinc-900">
              {record.gantung_verification_status === "verified"
                ? "✓ Bukti Verifikasi GANTUNG · Verified"
                : "⚠️ Verifikasi GANTUNG · Unverified"}
              {record.gantung_verification_confidence != null && (
                <span className="ml-2 text-xs font-mono text-zinc-500">
                  Confidence: {record.gantung_verification_confidence}%
                </span>
              )}
            </h2>
          </div>
          <div className="p-4 space-y-3 text-xs">
            {record.gantung_verification_status === "verified" && record.gantung_evidence_record_id && (
              <>
                <div className="space-y-1.5 font-mono">
                  <div className="flex justify-between gap-3">
                    <span className="text-zinc-500">Tipe bukti:</span>
                    <span className="text-zinc-900 font-semibold" data-testid="evidence-type">
                      {record.gantung_evidence_type === "adjustment_panel_record"
                        ? "Adjustment Depo (panel records)"
                        : record.gantung_evidence_type}
                    </span>
                  </div>
                  {record.gantung_evidence_amount != null && (
                    <div className="flex justify-between gap-3">
                      <span className="text-zinc-500">Nominal adjustment:</span>
                      <span className="text-emerald-700 font-bold">
                        Rp {Number(record.gantung_evidence_amount || 0).toLocaleString("id-ID")}
                      </span>
                    </div>
                  )}
                  {record.gantung_evidence_gap_hours != null && (
                    <div className="flex justify-between gap-3">
                      <span className="text-zinc-500">Gap dari WD asli:</span>
                      <span className="text-zinc-900">
                        {record.gantung_evidence_gap_hours < 24
                          ? `${record.gantung_evidence_gap_hours}j`
                          : `${Math.floor(record.gantung_evidence_gap_hours / 24)}h ${Math.round(record.gantung_evidence_gap_hours % 24)}j`}
                      </span>
                    </div>
                  )}
                  {record.gantung_evidence_approved_by && (
                    <div className="flex justify-between gap-3">
                      <span className="text-zinc-500">Approved by:</span>
                      <span className="text-zinc-900">{record.gantung_evidence_approved_by}</span>
                    </div>
                  )}
                </div>
                {record.gantung_evidence_summary && (
                  <p className="text-[11px] text-zinc-600 italic bg-white/70 border border-emerald-100 rounded-sm px-2 py-1.5">
                    {record.gantung_evidence_summary}
                  </p>
                )}
                <a href={`/records/${record.gantung_evidence_record_id}`}
                  target="_blank" rel="noreferrer"
                  data-testid="evidence-record-link"
                  className="inline-block text-emerald-700 font-mono text-[11px] underline hover:text-emerald-900">
                  → Buka adjustment record di tab baru
                </a>
              </>
            )}
            {record.gantung_verification_status === "unverified" && record.gantung_verification_note && (
              <p className="text-[11px] text-amber-800 leading-relaxed bg-white/70 border border-amber-200 rounded-sm px-3 py-2">
                {record.gantung_verification_note}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Match Candidates */}
      {record.candidates && record.candidates.length > 0 && (
        <div className="border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
            <h2 className="text-sm font-semibold text-zinc-900">Kandidat Match ({record.candidates.length})</h2>
          </div>
          <div className="divide-y divide-zinc-100">
            {record.candidates.map((c) => {
              const isClaimed = !!c.claimed_by;
              return (
              <div key={c.id} className={`p-4 ${candidateRowClass(c)}`} data-testid={`candidate-row-${c.row_reference}`}>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <StatusBadge status={c.match_type} />
                  <span className="text-sm font-mono font-bold">{c.score}%</span>
                  <span className="text-xs text-zinc-400 font-mono">{c.sheet_tab || c.sheet_source_name} / {c.row_reference}</span>
                  {c.amount_diff > 0 && <span className="text-xs text-amber-600 font-mono">diff: {formatIDR(c.amount_diff)}</span>}
                  {c.date_diff > 0 && <span className="text-xs text-blue-600 font-mono">{c.date_diff}d offset</span>}
                  <div className="ml-auto"><CandidateClaimBadge candidate={c} /></div>
                </div>
                {isClaimed && <div className="mb-2"><CandidateClaimInfo candidate={c} /></div>}
                {c.notes && <p className="text-xs text-zinc-500 mb-2">{c.notes}</p>}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
                  {c.candidate_payload && Object.entries(c.candidate_payload).filter(([k]) => !["entries", "split_details"].includes(k)).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-zinc-400">{k}:</span>{" "}
                      <span className="text-zinc-700">{k === "sheet_row" ? `Row ${v}` : (typeof v === "number" && k !== "sheet_row" ? formatIDR(v) : String(v))}</span>
                    </div>
                  ))}
                </div>
                {c.candidate_payload?.split_details && (
                  <p className="text-xs text-cyan-600 mt-1 font-mono">Split: {c.candidate_payload.split_details}</p>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Panel Raw Data */}
      {record.panel_raw && (
        <details className="border border-zinc-200 rounded-sm">
          <summary className="px-4 py-3 cursor-pointer text-sm font-semibold text-zinc-900 bg-zinc-50">Raw Panel Data</summary>
          <pre className="p-4 text-xs font-mono text-zinc-600 overflow-x-auto">{JSON.stringify(record.panel_raw, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

function IC({ label, value, mono }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</p>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-zinc-900 truncate`}>{value || "-"}</div>
    </div>
  );
}
